import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * ChangePasswordDialog — modale per il cambio password volontario dal profilo.
 * Riutilizza l'endpoint POST /auth/change-password già usato dal cambio forzato.
 */
export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const reset = () => {
    setCurrentPwd("");
    setNewPwd("");
    setConfirmPwd("");
    setError("");
    setDone(false);
  };

  const close = () => {
    onOpenChange?.(false);
    setTimeout(reset, 300);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (newPwd.length < 6) {
      setError("La nuova password deve essere di almeno 6 caratteri");
      return;
    }
    if (newPwd !== confirmPwd) {
      setError("Le due password non corrispondono");
      return;
    }
    if (newPwd === currentPwd) {
      setError("La nuova password deve essere diversa da quella attuale");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPwd,
        new_password: newPwd,
      });
      setDone(true);
      toast.success("Password aggiornata con successo");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange?.(true); }}>
      <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-blue-600" />
            Cambia password
          </DialogTitle>
          <DialogDescription>
            Aggiorna la tua password di accesso. Ti servirà per il prossimo login.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-6 text-center" data-testid="change-password-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
            <h3 className="font-heading font-semibold text-lg mt-3">Password aggiornata</h3>
            <p className="text-sm text-slate-600 mt-2">
              La tua nuova password è attiva. Ricordati di usarla al prossimo accesso.
            </p>
            <Button onClick={close} className="mt-5" data-testid="change-password-close">
              Chiudi
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="change-password-form">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Password attuale
              </Label>
              <Input
                type="password"
                value={currentPwd}
                onChange={(e) => setCurrentPwd(e.target.value)}
                required
                autoFocus
                autoComplete="current-password"
                data-testid="chpwd-current"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Nuova password (min 6 caratteri)
              </Label>
              <Input
                type="password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
                data-testid="chpwd-new"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Conferma nuova password
              </Label>
              <Input
                type="password"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
                data-testid="chpwd-confirm"
              />
            </div>
            {error && (
              <div
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                data-testid="chpwd-error"
              >
                {error}
              </div>
            )}
            <DialogFooter className="gap-2 sm:gap-2">
              <Button type="button" variant="outline" onClick={close} data-testid="chpwd-cancel">
                Annulla
              </Button>
              <Button type="submit" disabled={submitting} data-testid="chpwd-submit">
                {submitting ? "Aggiornamento..." : "Aggiorna password"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
